'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import TacticalboardDrawerFull from '@/components/tacticalboard/TacticalboardDrawerFull';
import { useGlobal } from '@/contexts/GlobalContext';

interface TacticalboardDrawerContextProps {
  isOpen: boolean;
  openDrawer: (content: ReactNode) => void;
  closeDrawer: () => void;
}

const TacticalboardDrawerContext = createContext<TacticalboardDrawerContextProps | undefined>(undefined);

export const TacticalboardDrawerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const context = useGlobal();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [drawerComponent, setDrawerComponent] = useState<React.ReactNode | null>(null);

  const openDrawer = (component: React.ReactNode) => {
    setDrawerComponent(component);
    setIsOpen(true);
  };

  const closeDrawer = async () => {
    setIsOpen(false);
    // NOTE: ドロワーのアニメーションが効かない対策
    // フラグ変更 -> ドロワー内容（コンポーネント）NULL -> レンダリング おそらくこの順で
    // 処理されており、アニメーションが効く前にコンポーネントDOMが存在しなくなってしまっている。
    // useEffectでのフラグ変更後のNULLでもだめ。なので以下の対応。
    // 300ミリ秒待機してからドロワーの内容をnullに設定
    // 上記の問題はドロワーコンテンツのコンポーネントのuseEffectでsetDrawerComponentを呼べば
    // このようなHACKは不要になると思われるがコンテンツのコンポーネント側の実装がその分増えるので
    // とりあえずはこれでいい気がする
    await new Promise((resolve) => setTimeout(resolve, 300));
    setDrawerComponent(null);
  };

  useEffect(() => {
    // NOTE: スクロールバーのレイアウトシフト対応
    // NOTE: ドロワーやモーダル要素が表示されていない（重なっていない）場合に初期化する
    if (context.overlapLevel === 0) {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.documentElement.style.setProperty('scrollbar-gutter', 'stable');
    }
  }, [context.overlapLevel]);

  useEffect(() => {
    if (isOpen) {
      context.handleIncrementOverlapLevel();

      // NOTE: スクロールバーのレイアウトシフト対応
      // NOTE: ドロワーやモーダル要素が表示する（重なる）場合にスクロールバー領域を調整する
      // 強制的にスクロールバーを表示させて幅を計算
      document.body.style.overflow = 'scroll';
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden'; // スクロールを無効化
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      document.documentElement.style.removeProperty('scrollbar-gutter');
    } else {
      context.handleDecrementOverlapLevel();
    }
  }, [isOpen]);

  useEffect(() => {
    closeDrawer();
  }, [location.pathname]);

  return (
    <TacticalboardDrawerContext.Provider value={{ isOpen, openDrawer, closeDrawer: () => void closeDrawer() }}>
      {children}
      <TacticalboardDrawerFull isOpen={isOpen} drawerComponent={drawerComponent} />
    </TacticalboardDrawerContext.Provider>
  );
};

export const useTacticalboardDrawer = () => {
  const context = useContext(TacticalboardDrawerContext);
  if (!context) {
    throw new Error('useDrawer must be used within a DrawerProvider');
  }
  return context;
};
